import React, { useState, useEffect } from "react";
import { Button, FormInput, FormSelect } from "../Common.jsx";
import { DynamicIcon } from "../icons.jsx";

export default function SupportConfigForm({ data, token, onSave, onNext }) {
    const [form, setForm] = useState({
        support_tier: data?.support_tier ?? "Basic",
        sla_description: data?.sla_description ?? "",
        response_time_hours: data?.response_time_hours ?? 24,
        dedicated_slack_channel: data?.dedicated_slack_channel ?? false,
    });

    useEffect(() => {
        setForm({
            support_tier: data?.support_tier ?? "Basic",
            sla_description: data?.sla_description ?? "",
            response_time_hours: data?.response_time_hours ?? 24,
            dedicated_slack_channel: data?.dedicated_slack_channel ?? false,
        });
    }, [data]);

    const update = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    const tierOptions = [
        { value: "Basic", label: "Basic (Email Only)", sla: "48h response" },
        { value: "Silver", label: "Silver (Email + Chat)", sla: "24h response" },
        { value: "Gold", label: "Gold (Priority 24/7)", sla: "4h response" },
        { value: "Platinum", label: "Platinum (Dedicated Agent)", sla: "1h response" },
    ];

    const handleSave = async () => {
        await onSave(form);
        if (onNext) onNext();
    };

    return (
        <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800 flex gap-3">
                <DynamicIcon name="LifeBuoy" className="text-blue-600 dark:text-blue-400 mt-1" />
                <div>
                    <h4 className="font-semibold text-blue-900 dark:text-blue-100">Support Entitlement</h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Define the SLA and support channels for this tenant.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Support Tier</label>
                    <div className="space-y-2">
                        {tierOptions.map((tier) => (
                            <div
                                key={tier.value}
                                onClick={() => update("support_tier", tier.value)}
                                className={`cursor-pointer p-3 rounded-lg border flex justify-between items-center transition-all ${form.support_tier === tier.value
                                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                                    : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                                    } `}
                            >
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">{tier.label}</p>
                                    <p className="text-xs text-gray-500">{tier.sla}</p>
                                </div>
                                {form.support_tier === tier.value && <DynamicIcon name="Check" className="text-primary w-5 h-5" />}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-4">
                    <FormSelect
                        label="Response Time (Hours)"
                        value={form.response_time_hours}
                        onChange={(e) => update("response_time_hours", parseInt(e.target.value))}
                    >
                        <option value={1}>1 Hour (Critical)</option>
                        <option value={4}>4 Hours (High)</option>
                        <option value={12}>12 Hours (Medium)</option>
                        <option value={24}>24 Hours (Standard)</option>
                        <option value={48}>48 Hours (Low)</option>
                    </FormSelect>

                    <FormInput
                        label="SLA Description"
                        value={form.sla_description}
                        onChange={(e) => update("sla_description", e.target.value)}
                        placeholder="e.g. 99.9% Uptime Guarantee"
                    />

                    <div className="pt-2">
                        <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                                type="checkbox"
                                className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                                checked={form.dedicated_slack_channel}
                                onChange={(e) => update("dedicated_slack_channel", e.target.checked)}
                            />
                            <span className="text-gray-900 dark:text-gray-100 font-medium">Dedicated Slack Channel</span>
                        </label>
                        <p className="text-xs text-gray-500 pl-8 mt-1">Automatically invites tenant admins to a shared Connect channel.</p>
                    </div>
                </div>
            </div>

            <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-t dark:border-gray-700 p-4 -mx-6 -mb-6 mt-8 flex justify-end shadow-lg rounded-b-xl">
                <Button onClick={handleSave} className="px-6 shadow-md">
                    Save & Next <DynamicIcon name="ArrowRight" className="ml-2 w-4 h-4" />
                </Button>
            </div>
        </div>
    );
}
